// row 108 MaskedRow and MaskedHistoryRow (DC-28): a security-trimmed row. A requisition relationship outside the viewer's
// scope shows as one row of --text-3 on --t-neutral with the words *subject to permission*: no title, no link, nothing
// focusable. MaskedRow is a table row (core's Tr masked) whose words span the columns after any leading cells (a date);
// MaskedHistoryRow is the same in the event list (#69). A masked group (DS #69: several trimmed events in a run) is one
// row with `count`, the number beside the words; the number is data, so no word is added for it.

import type { ReactNode } from "react";
import { Count } from "../core/Count";
import { Icon } from "../core/icons";
import { Td, Tr } from "../core/Table";
import { cx } from "../lib/cx";
import { MASKED_LABELS } from "./labels";

export interface MaskedRowProps {
  /** The table's column count. */
  cols: number;
  /** Cells kept before the words (a date): data the viewer may see. */
  lead?: readonly ReactNode[] | undefined;
  className?: string | undefined;
}

function Words() {
  return (
    <span className="mc-masked-w">
      <Icon name="lock" size={14} />
      {MASKED_LABELS.subjectToPermission}
    </span>
  );
}

export function MaskedRow({ cols, lead = [], className }: MaskedRowProps) {
  return (
    <Tr masked className={className}>
      {lead.map((cell, index) => <Td key={index}>{cell}</Td>)}
      <Td colSpan={Math.max(1, cols - lead.length)}><Words /></Td>
    </Tr>
  );
}

export interface MaskedHistoryRowProps {
  /** When it happened, if the viewer may see that much. */
  when?: ReactNode | undefined;
  /** A masked group: how many trimmed events this one row stands for (drawn when more than one). */
  count?: number | undefined;
  className?: string | undefined;
}

export function MaskedHistoryRow({ when, count, className }: MaskedHistoryRowProps) {
  return (
    <li className={cx("mc-hrow", "mc-masked", className)}>
      <span className="mc-hdot" aria-hidden="true" />
      <div className="mc-hbody">
        <Words />
        {count !== undefined && count > 1 ? <Count n={count} className="mc-masked-n" /> : null}
        {when !== undefined && when !== null ? <span className="mc-hwhen">{when}</span> : null}
      </div>
    </li>
  );
}
